import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  Pagination,
  Popover,
  Table,
  message,
} from "antd";
import { PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { HeaderTable, PermissionDirect } from "../../Components";
import {
  getPermissionRegions,
  TruckVendorApi,
  VehicleApi,
} from "../../services";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import { useRequestLock } from "../../shared/hooks/useRequestLock";
import {
  buildVehiclePayload,
  createEmptyVehicleRow,
  hasVehicleRequiredFields,
  normalizeTruckVendorOption,
  normalizeVehicleRows,
  runVehicleRequest,
  VEHICLE_NEW_ROW_KEY,
} from "./helpers";
import { getVehicleColumns, renderVehicleOilTable } from "./columns";
import type { PermissionRegion, SelectOption, VehicleRow } from "./types";
import styles from "./Vehicle.module.scss";

const { confirm } = Modal;

const baseColumnOptions = [
  { label: "Biển số xe", value: "licensePlate" },
  { label: "Nhà xe", value: "truckVendorName" },
  { label: "Hình thức sở hữu", value: "ownership" },
  { label: "Dung tích bình dầu", value: "oilTankVolume" },
  { label: "Trạng thái", value: "status" },
];

const prependUniqueRowKey = (rowKey: React.Key, keys: React.Key[]) =>
  keys.includes(rowKey) ? keys : [rowKey, ...keys];

const Vehicle = () => {
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const canAdd = checkPermissions("VEHICLE", "add", features);
  const canEdit = checkPermissions("VEHICLE", "edit", features);
  const canDelete = checkPermissions("VEHICLE", "delete", features);
  const [messageApi, contextHolder] = message.useMessage();
  const { isLocked, runLocked } = useRequestLock();

  const [dataSource, setDataSource] = useState<VehicleRow[]>([]);
  const [regions, setRegions] = useState<PermissionRegion[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchTruckVendorOptions = (search: string): Promise<SelectOption[]> =>
    TruckVendorApi.getAllTruckVendorSearch({
      searchText: search,
    }).then(
      (res: any) =>
        res?.data?.map((item: any) => ({
          label: item.shortName ?? item.name,
          value: item._id,
          record: item,
        })) ?? [],
    );

  const fetchVehicles = async (
    nextPagination = pagination,
    nextRegions = regions,
  ) => {
    setLoading(true);
    try {
      const response = await runVehicleRequest(
        () =>
          VehicleApi.getAllVehicleV2({
            current: nextPagination.current,
            pageSize: nextPagination.pageSize,
          }),
        (errorMessage) => messageApi.error(errorMessage),
        "Tải danh sách phương tiện thất bại.",
      );
      if (!response) return;

      const rows = normalizeVehicleRows(response?.data ?? [], nextRegions);
      setDataSource(rows);
      setPagination((prev) => ({
        ...prev,
        current: response?.current ?? nextPagination.current,
        pageSize: response?.pageSize ?? nextPagination.pageSize,
        total: response?.totalItem ?? 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const permissionRegions = await runVehicleRequest(
        () => getPermissionRegions({}),
        (errorMessage) => messageApi.error(errorMessage),
        "Tải vùng quyền thất bại.",
      );
      if (!permissionRegions) return;

      setRegions(permissionRegions);
      await fetchVehicles(pagination, permissionRegions);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (editingKey) {
      messageApi.warning("Vui lòng lưu hoặc hủy dòng đang sửa trước.");
      return;
    }
    const newRow = createEmptyVehicleRow(regions);
    setDataSource((prev) => [newRow, ...prev]);
    setEditingKey(VEHICLE_NEW_ROW_KEY);
    setExpandedRowKeys((prev) => prependUniqueRowKey(VEHICLE_NEW_ROW_KEY, prev));
  };

  const handleEdit = (record: VehicleRow) => {
    if (editingKey) {
      messageApi.warning("Vui lòng lưu hoặc hủy dòng đang sửa trước.");
      return;
    }
    setEditingKey(record.key);
    setExpandedRowKeys((prev) => prependUniqueRowKey(record.key, prev));
  };

  const handleCancel = () => {
    if (editingKey === VEHICLE_NEW_ROW_KEY) {
      setDataSource((prev) =>
        prev.filter((item) => item.key !== VEHICLE_NEW_ROW_KEY),
      );
    } else {
      fetchVehicles();
    }
    setEditingKey(null);
  };

  const handleFieldChange = (
    rowKey: string,
    field: keyof VehicleRow,
    value: any,
  ) => {
    setDataSource((prev) =>
      prev.map((item) => {
        if (item.key !== rowKey) return item;
        if (field === "truckVendor") {
          const truckVendor = normalizeTruckVendorOption(value);
          return {
            ...item,
            truckVendor,
            truckVendorName: truckVendor.label,
          };
        }
        return {
          ...item,
          [field]: value,
        };
      }),
    );
  };

  const handleOilChange = (
    rowKey: string,
    region: string,
    field: any,
    value: number | null,
  ) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === rowKey
          ? {
              ...item,
              oil: item.oil.map((oilRow) =>
                oilRow.region === region
                  ? {
                      ...oilRow,
                      consumption: {
                        ...oilRow.consumption,
                        [field]: value ?? 0,
                      },
                    }
                  : oilRow,
              ),
            }
          : item,
      ),
    );
  };

  const handleSave = async (record: VehicleRow) => {
    await runLocked(`vehicle:${record.key}`, async () => {
      if (!hasVehicleRequiredFields(record)) {
        messageApi.error(
          "Vui lòng nhập biển số xe, nhà xe và thông tin tiêu hao.",
        );
        return;
      }

      const payload = buildVehiclePayload(record);
      const response = await runVehicleRequest(
        () =>
          record.state === "new" || !record._id
            ? VehicleApi.postCreateVehicleV2(payload)
            : VehicleApi.putUpdateVehicleV2(record._id, payload),
        (errorMessage) => messageApi.error(errorMessage),
        "Lưu phương tiện thất bại.",
      );
      if (!response) return;

      if (response?.success !== false) {
        messageApi.success("Lưu phương tiện thành công.");
        setEditingKey(null);
        await fetchVehicles();
      }
    });
  };

  const handleDelete = (record: VehicleRow) => {
    if (!record._id) return;
    confirm({
      title: "Xóa phương tiện",
      content: `Bạn có chắc chắn muốn xóa xe ${record.licensePlate}?`,
      okText: "Xóa",
      cancelText: "Hủy",
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await runVehicleRequest(
          () => VehicleApi.deleteVehicleV2(record._id as string),
          (errorMessage) => messageApi.error(errorMessage),
          "Xóa phương tiện thất bại.",
        );
        if (!response) return;

        if (response?.success !== false) {
          messageApi.success("Xóa phương tiện thành công.");
          setEditingKey(null);
          setExpandedRowKeys((prev) =>
            prev.filter((key) => key !== record.key),
          );
          await fetchVehicles();
        }
      },
    });
  };

  const handleColumnVisibilityChange = (
    dataIndex: string,
    checked: boolean,
  ) => {
    setHiddenColumns((prev) =>
      checked
        ? prev.filter((item) => item !== dataIndex)
        : [...prev, dataIndex],
    );
  };

  const columns = getVehicleColumns({
    editingKey,
    canEdit,
    canDelete,
    hiddenColumns,
    fetchTruckVendorOptions,
    onEdit: handleEdit,
    onSave: handleSave,
    isSaveLocked: (record) =>
      isLocked(`vehicle:${record.key}`),
    onCancel: handleCancel,
    onDelete: handleDelete,
    onFieldChange: handleFieldChange,
    onOilChange: handleOilChange,
  });

  const renderColumnSetting = () => (
    <Popover
      placement="bottomRight"
      trigger="click"
      content={
        <div style={{ minWidth: 180 }}>
          {baseColumnOptions.map((item) => (
            <div key={item.value} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={!hiddenColumns.includes(item.value)}
                onChange={(event) =>
                  handleColumnVisibilityChange(item.value, event.target.checked)
                }
              >
                {item.label}
              </Checkbox>
            </div>
          ))}
        </div>
      }
    >
      <Button icon={<SettingOutlined />}>Cột hiển thị</Button>
    </Popover>
  );

  useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <PermissionDirect screen={["VEHICLE"]}>
      {contextHolder}
      <div className={styles.Vehicle}>
        <div className="container">
          <HeaderTable
            title="Danh sách Phương tiện"
            buttons={[
              {
                children: "Thêm mới",
                type: "primary",
                icon: <PlusOutlined />,
                disabled: !canAdd || Boolean(editingKey),
                onClick: handleAdd,
              },
            ]}
          />
          <div className={styles.Toolbar}>{renderColumnSetting()}</div>
          <Table
            rowKey="key"
            bordered
            loading={loading}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="small"
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
              expandedRowRender: (record) =>
                renderVehicleOilTable(
                  record,
                  editingKey,
                  canEdit,
                  handleOilChange,
                ),
            }}
          />
          <Pagination
            className={styles.Pagination}
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            onChange={(current, pageSize) => {
              const nextPagination = {
                ...pagination,
                current,
                pageSize,
              };
              setPagination(nextPagination);
              fetchVehicles(nextPagination);
            }}
          />
        </div>
      </div>
    </PermissionDirect>
  );
};

export default Vehicle;
